import { handleError } from '@/lib/errors';
import { createStore } from 'cross-state';
import { formatBytes } from 'lib/format/sizeFormat';
import mergeCollections from 'lib/mergeCollections';
import type { Database } from 'lib/models/database';
import type { State } from 'lib/models/state';
import io from 'socket.io-client';

export class ConnectionError extends Error {
  name = 'ConnectionError';

  constructor(message: string, cause?: unknown) {
    super(message, { cause });
  }
}

export interface ConnectionState {
  enabled: boolean;
  connected: boolean;
  error?: unknown;
}

const stateApi = new (class StateApi {
  connection = createStore<ConnectionState>({ enabled: false, connected: false });

  state = createStore<State>(
    {
      definitions: {
        databases: new Map(),
        collections: new Map(),
      },
      actual: {
        collections: new Map(),
      },
      jobs: {
        jobs: new Map(),
      },
    },
    {
      retain: { seconds: 10 },
      effect: (store) => {
        this.connection.set({ enabled: true, connected: false, error: undefined });

        const socket = io({
          query: { version: store.version },
          reconnectionAttempts: Infinity,
          reconnectionDelay: 5_000,
          reconnectionDelayMax: 60_000,
        });

        socket.io.on('open', () => {
          this.connection.set({ enabled: true, connected: true, error: undefined });
          console.debug('[ws] connected');
        });

        socket.io.on('reconnect_attempt', () => {
          socket.io.opts.query = { version: store.version };
          console.debug('[ws] reconnect attempt');
        });

        socket.io.on('close', (reason, description) => {
          this.connection.set('connected', false);
          console.debug('[ws] disconnected', reason, description);
        });

        socket.on('disconnect', (reason) => {
          if (reason === 'io server disconnect') {
            socket.connect();
          }
        });

        socket.io.on('error', (error) => {
          this.connection.set('error', new ConnectionError('Connection could not be established', error));
          console.debug('[ws] error', error);
        });

        socket.on('sync', (message) => {
          try {
            store.acceptSync(message);
          } catch (error) {
            handleError(error);
          }
        });

        return () => {
          console.debug('[ws] disposing connection');

          socket.close();
          this.connection.set({ enabled: false, connected: false, error: undefined });
        };
      },
    },
  );

  mergedCollections = this.state.map<Database[]>((state) => mergeCollections(state));

  size() {
    const state = [this.state.get(), this.mergedCollections.get()];
    return formatBytes(JSON.stringify(state).length);
  }
})();

export default stateApi;
Object.assign(window, { stateApi });
